/**
 * Finding text in the open reading log (日本-603).
 *
 * The browser's Ctrl+F stopped being able to answer this for two reasons, and each on its own
 * would have been enough:
 *
 * - **The list is windowed** (日本-602). Of a 2,000-sentence log, about twelve rows are in the DOM,
 *   so nothing outside them can be found at all.
 * - **Furigana breaks matches on the rows it CAN see**, and has since 日本-512. The `<rt>` text sits
 *   in the DOM between a kanji and its okurigana, so `共に` and `飛び去った` were never found while
 *   `終戦` was. Measured with `window.find` on the running app.
 *
 * So this matches against the sentences in memory, never the DOM. It is pure, for the reason
 * `logWindow.ts` is: `npm test` has no DOM, and `checks/check-read.mjs` covers the wiring.
 *
 * **Offsets are CODE POINTS, not UTF-16 units**, because `Sentence` addresses characters by code
 * point (`[...segment.text]`) and so do the server's token offsets. With `indexOf`'s units, a
 * surrogate-pair kanji such as 𠮟 early in a sentence would shift every mark after it by one.
 */

/** The two things a row carries that can be searched. */
export type HitField = "text" | "translation";

export interface Hit {
  entryId: string;
  /** Position of the row in the log, so a jump can scroll to it without a second lookup. */
  row: number;
  field: HitField;
  /** Code-point offsets into that field, end exclusive. */
  start: number;
  end: number;
}

export interface Searchable {
  id: string;
  text: string;
  translation: string;
}

/**
 * ASCII letters only. `toLowerCase` on the whole string is the obvious version and is wrong here:
 * a handful of characters change LENGTH when lowercased (`İ` → `i̇`), which would move every offset
 * after them. Japanese has no case, so the only case a query can usefully ignore is a translation's.
 */
function fold(char: string): string {
  const code = char.charCodeAt(0);
  return code >= 65 && code <= 90 && char.length === 1 ? String.fromCharCode(code + 32) : char;
}

function occurrences(haystack: readonly string[], needle: readonly string[]): number[] {
  const found: number[] = [];
  if (needle.length === 0 || needle.length > haystack.length) return found;
  outer: for (let i = 0; i + needle.length <= haystack.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (fold(haystack[i + j] ?? "") !== needle[j]) continue outer;
    }
    found.push(i);
    // Non-overlapping, as browser find is: `ああ` in `あああ` is one hit, not two.
    i += needle.length - 1;
  }
  return found;
}

/**
 * Every hit, in reading order: row by row, the Japanese before the translation, left to right.
 *
 * A query of only whitespace finds nothing rather than every space in the log.
 */
export function findHits(entries: readonly Searchable[], query: string): Hit[] {
  if (query.trim() === "") return [];
  const needle = [...query].map(fold);
  const hits: Hit[] = [];
  entries.forEach((entry, row) => {
    for (const field of ["text", "translation"] as const) {
      const value = entry[field];
      if (!value) continue;
      for (const start of occurrences([...value], needle)) {
        hits.push({ entryId: entry.id, row, field, start, end: start + needle.length });
      }
    }
  });
  return hits;
}

/**
 * The hit to land on while typing: the first at or after `fromRow`, wrapping to the first hit in
 * the log when there is none below. Null when there are no hits.
 *
 * `fromRow` is the current hit's row while one is showing, so narrowing `共` to `共に` stays where
 * you are; otherwise it is the top visible row, which is where Ctrl+F starts too.
 */
export function anchorHit(hits: readonly Hit[], fromRow: number): number | null {
  if (hits.length === 0) return null;
  const at = hits.findIndex((hit) => hit.row >= fromRow);
  return at === -1 ? 0 : at;
}

/** The next or previous hit, wrapping at both ends. */
export function stepHit(current: number | null, count: number, dir: 1 | -1): number | null {
  if (count === 0) return null;
  if (current === null) return dir === 1 ? 0 : count - 1;
  return (current + dir + count) % count;
}

/** What one field of one row draws. `current` is the index into `ranges` of the current hit. */
export interface FieldMarks {
  ranges: readonly (readonly [number, number])[];
  current: number | null;
}

export interface RowMarks {
  text?: FieldMarks;
  translation?: FieldMarks;
}

/**
 * The marks for every row that has a hit, keyed by entry id.
 *
 * **Rows with no hits are absent**, and `Read` hands those rows a constant `undefined`: `Sentence`
 * is memoised, and a fresh empty object per row per keystroke would re-render every drawn row on
 * every character typed. Only the rows whose marks actually changed get a new object.
 */
export function marksByEntry(hits: readonly Hit[], current: number | null): Map<string, RowMarks> {
  const byEntry = new Map<string, { text: [number, number][]; translation: [number, number][]; cur: { field: HitField; at: number } | null }>();
  hits.forEach((hit, index) => {
    let row = byEntry.get(hit.entryId);
    if (!row) {
      row = { text: [], translation: [], cur: null };
      byEntry.set(hit.entryId, row);
    }
    const list = row[hit.field];
    if (index === current) row.cur = { field: hit.field, at: list.length };
    list.push([hit.start, hit.end]);
  });
  const out = new Map<string, RowMarks>();
  for (const [id, row] of byEntry) {
    const marks: RowMarks = {};
    for (const field of ["text", "translation"] as const) {
      if (row[field].length > 0) {
        marks[field] = {
          ranges: row[field],
          current: row.cur?.field === field ? row.cur.at : null,
        };
      }
    }
    out.set(id, marks);
  }
  return out;
}

/** Which mark, if any, covers code-point offset `at`. */
export function markAt(marks: FieldMarks | undefined, at: number): "hit" | "current" | null {
  return markEdgesAt(marks, at)?.kind ?? null;
}

/**
 * The mark at `at`, and whether this character opens or closes it.
 *
 * `Sentence` draws a span per CHARACTER, so a ring drawn on every span is a box around each glyph —
 * `と|共|に` with bars between them — where the canvas draws one outline around the run. The edges
 * say which sides of this character's box belong to the outline.
 */
export function markEdgesAt(
  marks: FieldMarks | undefined,
  at: number,
): { kind: "hit" | "current"; first: boolean; last: boolean } | null {
  if (!marks) return null;
  for (let i = 0; i < marks.ranges.length; i++) {
    const range = marks.ranges[i];
    if (!range) continue;
    const [start, end] = range;
    if (at >= start && at < end) {
      return { kind: i === marks.current ? "current" : "hit", first: at === start, last: at === end - 1 };
    }
  }
  return null;
}

/**
 * A plain string split into runs by its marks, for the two places that draw text without
 * per-character spans: a row still waiting for its parse, and the 訳 line.
 */
export function markRuns(
  value: string,
  marks: FieldMarks | undefined,
): { text: string; mark: "hit" | "current" | null }[] {
  const chars = [...value];
  if (!marks || marks.ranges.length === 0) return [{ text: value, mark: null }];
  const runs: { text: string; mark: "hit" | "current" | null }[] = [];
  chars.forEach((char, i) => {
    const mark = markAt(marks, i);
    const last = runs[runs.length - 1];
    if (last && last.mark === mark) last.text += char;
    else runs.push({ text: char, mark });
  });
  return runs;
}
